import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useFormatCurrency } from "@/context/CountryContext";
import { useGeo } from "@/hooks/useGeo";
import MapView from "@/components/MapView";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, Navigation, Store, Trophy } from "lucide-react";

export default function ProductCompare() {
  const fmt = useFormatCurrency();
  const [params] = useSearchParams();
  const name = params.get("name") || "";
  const { geo } = useGeo();
  const [data, setData] = useState(null);
  const [route, setRoute] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    const p = { name };
    if (geo) { p.lat = geo.lat; p.lng = geo.lng; }
    const { data } = await api.get("/products/compare", { params: p });
    setData(data);
  }, [name, geo]);

  useEffect(() => { if (name) load(); }, [name, geo]); // eslint-disable-line

  const itinerary = async (offer) => {
    setSelected(offer.product_id);
    const s = offer.shop;
    // If shop has GPS coords, try OSRM routing on the map
    if (geo && s?.lat && s?.lng) {
      try {
        const url = `https://router.project-osrm.org/route/v1/driving/${geo.lng},${geo.lat};${s.lng},${s.lat}?overview=full&geometries=geojson`;
        const res = await fetch(url);
        const json = await res.json();
        const coords = json.routes?.[0]?.geometry?.coordinates?.map((c) => [c[1], c[0]]) || [];
        setRoute(coords);
        return;
      } catch { setRoute([[geo.lat, geo.lng], [s.lat, s.lng]]); return; }
    }
    // Fallback: open Google Maps with the shop's address
    const dest = encodeURIComponent(s?.address || `${s?.name}, ${s?.city}`);
    const origin = geo ? `&origin=${geo.lat},${geo.lng}` : "";
    window.open(`https://www.google.com/maps/dir/?api=1${origin}&destination=${dest}`, "_blank");
  };

  if (!data) return <div className="max-w-5xl mx-auto px-6 py-20 text-center text-muted-foreground">Chargement…</div>;

  const shops = data.offers.map((o) => ({ ...o.shop, distance_km: o.distance_km }));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-secondary">{data.name}</h1>
      <p className="text-muted-foreground mt-1">{data.count} commerçant(s) · Meilleur prix <span className="text-primary font-bold">{data.best_price != null ? fmt(data.best_price) : "—"}</span></p>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="space-y-3">
          {data.offers.map((o, i) => {
            const best = o.price === data.best_price;
            return (
              <div key={o.product_id} data-testid={`offer-${o.product_id}`}
                className={`p-4 rounded-lg border bg-card flex items-center gap-4 transition-transform hover:-translate-y-0.5 ${best ? "border-accent ring-1 ring-accent" : "border-border"}`}>
                <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center shrink-0"><Store className="w-6 h-6 text-secondary" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-secondary truncate">{o.shop.name}</span>
                    {best && <Badge className="bg-accent text-accent-foreground text-[10px]"><Trophy className="w-3 h-3 mr-1" />Moins cher</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <span>{o.shop.city}</span>
                    {o.distance_km != null && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{o.distance_km} km</span>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display font-bold text-lg text-primary">{fmt(o.price)}</div>
                  <Button size="sm" variant="outline" className="mt-1 h-7 text-xs" data-testid={`route-${o.product_id}`} onClick={() => itinerary(o)}>
                    <Navigation className="w-3 h-3 mr-1" />Itinéraire
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="lg:sticky lg:top-20 h-fit">
          <MapView shops={shops} user={geo} route={route} height={420} />
          <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
            <Navigation className="w-3 h-3" />Cliquez « Itinéraire » pour tracer le trajet (OpenStreetMap / OSRM).
          </p>
        </div>
      </div>
    </div>
  );
}
